

import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const PricingPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Cybersecurity Pricing for Mortgage Companies | Mortgage Moat',
    description: 'Transparent cybersecurity pricing for mortgage firms. Phishing simulation from $100, managed services at $20/user/month, risk assessment at $250. No hidden fees.',
    keywords: 'cybersecurity pricing, mortgage security cost, managed security services pricing, phishing simulation cost, risk assessment price',
    canonical: '/pricing',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${SITE_URL}/pricing` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Mortgage Moat Cybersecurity Products & Pricing',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'Offer',
              name: 'Phishing Simulation + 2-Week Managed Services Trial',
              price: '100',
              priceCurrency: 'USD',
              url: `${SITE_URL}/phishing-simulation`,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'Offer',
              name: '24/7 Managed Cyber Protection',
              price: '20',
              priceCurrency: 'USD',
              url: `${SITE_URL}/managed-services`,
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'Offer',
              name: 'Security Risk Assessment',
              price: '250',
              priceCurrency: 'USD',
              url: `${SITE_URL}/risk-assessment`,
            },
          },
          {
            '@type': 'ListItem',
            position: 4,
            item: {
              '@type': 'Offer',
              name: 'IT/Cyber Security Policies & Procedures Manual',
              price: '395',
              priceCurrency: 'USD',
              url: `${SITE_URL}/security-policies-manual`,
            },
          },
          {
            '@type': 'ListItem',
            position: 5,
            item: {
              '@type': 'Offer',
              name: 'Complete Security Management',
              price: '179',
              priceCurrency: 'USD',
              url: `${SITE_URL}/complete-security-management`,
            },
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Are there any setup fees or hidden costs?',
            acceptedAnswer: { '@type': 'Answer', text: 'No. All our pricing is transparent with no setup fees, hidden costs, or surprise charges. What you see is what you pay.' },
          },
          {
            '@type': 'Question',
            name: 'Can I switch plans or cancel anytime?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. You can upgrade, downgrade, or cancel your subscription at any time. For monthly plans, there are no long-term contracts or cancellation fees.' },
          },
          {
            '@type': 'Question',
            name: 'Is there a money-back guarantee?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer a 30-day money-back guarantee on all subscription services. If you\'re not satisfied, we\'ll refund your payment, no questions asked.' },
          },
        ],
      },
    ],
  });

  const products = [
    {
      id: 1,
      category: 'Free Trial',
      name: 'Phishing Protection + 24/7 Managed Services Trial',
      description: 'Test your team\'s security awareness with a company-wide phishing simulation and get 2 weeks of free access to our complete 24/7 Managed Cyber Protection service',
      price: '$100',
      priceUnit: '',
      originalPrice: null,
      badge: 'Start Here',
      badgeColor: 'bg-green-600',
      image: 'https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/45d9a998ef2d8c1fc747fae2da1161f7.png',
      features: [
        'Company-wide phishing simulation',
        '2-week free trial of 24/7 Managed Cyber Protection',
        'Complete feature trial access',
        'Team vulnerability report',
        'No credit card required for trial',
        'Instant setup'
      ],
      cta: 'Get Phishing Simulation',
      link: '/purchase/phishing-simulation'
    },
    {
      id: 2,
      category: 'Managed Services',
      name: '24/7 Managed Cyber Protection',
      description: 'Scalable per-user security with complete managed services including round-the-clock monitoring, threat response, and continuous phishing simulations',
      price: '$20',
      priceUnit: '/user/month',
      originalPrice: null,
      badge: 'Most Popular',
      badgeColor: 'bg-red-600',
      image: 'https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/94d0f71b6ceceeac4b6e7dd2fdc9f1fe.png',
      features: [
        '24/7 security monitoring per user',
        'Endpoint protection',
        'Email security gateway',
        'Monthly phishing simulations',
        'Quarterly security training',
        'Vulnerability scanning & patching',
        'Incident response team',
        'Security operations center (SOC)',
        'Real-time threat alerts',
        'Monthly executive reports',
        'Compliance documentation',
        'Dedicated security portal',
        'Dark web monitoring',
        'Vendor risk management',
        'Policy updates & maintenance',
        'Unlimited support'
      ],
      cta: 'Subscribe Now',
      link: '/purchase/managed-services',
      note: 'Scalable pricing: Volume discounts available for 20+ users. Price decreases as your team grows.'
    },
    {
      id: 4,
      category: 'Assessment',
      name: 'Security Risk Assessment',
      description: 'Comprehensive security evaluation identifying vulnerabilities and ensuring regulatory compliance',
      price: '$250',
      priceUnit: '',
      originalPrice: '$499',
      badge: 'Best Value',
      badgeColor: 'bg-purple-600',
      image: 'https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/963c7c9b418cb06d34049bee49f33662.png',
      features: [
        'Full security infrastructure audit',
        'Policy and procedure review',
        'Employee security practice evaluation',
        'Vendor risk assessment',
        'Compliance gap analysis (GLBA, FFIEC)',
        'Detailed risk report with scores',
        'Prioritized remediation roadmap',
        'Executive summary for board',
        '90-minute consultation call',
        '30 days of follow-up support'
      ],
      cta: 'Get Assessment',
      link: '/risk-assessment',
      note: 'Results delivered in 7 days or less'
    },
    {
      id: 7,
      category: 'Policies & Procedures',
      name: 'IT/Cyber Security Policies & Procedures Manual',
      description: 'Comprehensive, customizable security policies and procedures manual covering all IT/cybersecurity best practices and regulatory compliance requirements',
      price: '$395',
      priceUnit: '',
      originalPrice: null,
      badge: 'Foundation',
      badgeColor: 'bg-teal-600',
      image: 'https://public.readdy.ai/ai/img_res/0b7634fb511674a4f8df178218d105b3.jpg',
      features: [
        'Complete IT/Cyber security policies manual',
        'Customizable for your organization',
        'GLBA & FFIEC compliance coverage',
        'Best practice security procedures',
        'Incident response protocols',
        'Access control policies',
        'Data protection guidelines',
        'Vendor management procedures',
        'Employee security policies',
        'Disaster recovery planning',
        'Regular updates included',
        'Implementation guidance'
      ],
      cta: 'Get Policy Manual',
      link: '/schedule-call',
      note: 'Fully customizable to meet your specific compliance needs'
    },
    {
      id: 5,
      category: 'Complete Package',
      name: 'Complete IT Security Package',
      description: 'Everything you need to establish comprehensive security: policies, assessment, and training all in one complete bundle',
      price: '$495',
      priceUnit: '',
      originalPrice: null,
      badge: 'All-in-One',
      badgeColor: 'bg-blue-600',
      image: 'https://readdy.ai/api/search-image?query=comprehensive%20cybersecurity%20package%20with%20policy%20documents%20security%20assessment%20reports%20and%20training%20materials%20displayed%20on%20modern%20desk%20in%20professional%20office%20environment%20with%20organized%20business%20setting%20and%20clean%20minimalist%20background&width=400&height=300&seq=complete-package1&orientation=landscape',
      features: [
        'Complete IT/Cyber Security Policies & Procedures Manual',
        'Full Security Risk Assessment',
        'Employee Security Awareness Training',
        'Customizable policy templates',
        'Implementation guidance',
        'Compliance documentation',
        'Training certificates',
        'Ongoing policy updates for 1 year',
        '60-day support included'
      ],
      cta: 'Get Complete Package',
      link: '/schedule-call',
      note: 'One-time payment - everything you need to get started'
    },
    {
      id: 6,
      category: 'Bundled Subscription',
      name: 'Complete Security Management',
      description: 'All-inclusive monthly subscription covering every aspect of your security needs - from policies to incident response',
      price: '$179',
      priceUnit: '/month',
      originalPrice: null,
      badge: 'Everything Included',
      badgeColor: 'bg-indigo-600',
      image: 'https://readdy.ai/api/search-image?query=comprehensive%20cybersecurity%20management%20service%20with%20security%20professionals%20reviewing%20documents%20conducting%20audits%20and%20training%20employees%20in%20modern%20professional%20office%20environment%20with%20organized%20workflow%20and%20clean%20business%20background&width=400&height=300&seq=bundled-service1&orientation=landscape',
      features: [
        'Document reviews & policy management',
        'Regular security audits',
        'Employee awareness training',
        'IT/Cyber security policies & procedures',
        'Vulnerability management & scanning',
        'Incident mitigation & remediation',
        'Compliance monitoring & reporting',
        'Monthly security assessments',
        'Policy updates & maintenance',
        'Priority support & consultation',
        'Remediation guidance',
        'Executive reporting'
      ],
      cta: 'Start Subscription',
      link: '/schedule-call',
      note: 'Complete security management in one monthly subscription'
    }
  ];

  const addons = [
    {
      name: 'Incident Response',
      description: 'Expert assistance to mitigate and remediate security incidents when they occur',
      pricing: '$249'
    },
    {
      name: 'Compliance Consulting',
      description: 'Expert guidance for GLBA, FFIEC, and other regulatory requirements',
      pricing: '$100/hour'
    },
    {
      name: 'Security Policy Development',
      description: 'Custom security policies and procedures for your organization',
      pricing: 'Starting at $349'
    }
  ];

  const handleCTA = (link: string) => {
    if (link === '/') {
      const element = document.getElementById('protection-level');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById('protection-level');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      navigate(link);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Complete Cybersecurity Solutions for Mortgage Companies
            </h1>
            <p className="text-xl mb-8 text-white/90">
              From free trials to enterprise protection – find the perfect security solution for your business. Transparent pricing, no hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Comparison */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">$100</div>
              <p className="text-gray-600 font-semibold">Phishing simulation + trial</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-600 mb-2">$20/user</div>
              <p className="text-gray-600 font-semibold">Full protection/month</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">$250</div>
              <p className="text-gray-600 font-semibold">Risk assessment</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-600 mb-2">$25/user</div>
              <p className="text-gray-600 font-semibold">Team training/month</p>
            </div>
          </div>
        </div>
      </section>

      {/* All Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              All Products & Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the security solutions that fit your needs. Mix and match services or get complete protection with our managed services package.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-product-shop>
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                {/* Badge */}
                <div className={`${product.badgeColor} text-white text-sm font-bold px-6 py-3 flex items-center justify-between`}>
                  <span>{product.badge}</span>
                  <span className="text-xs opacity-90">{product.category}</span>
                </div>
                
                {/* Product Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Product Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-6">{product.description}</p>

                  {/* Pricing */}
                  <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-gray-200">
                    <span className="text-4xl font-bold text-gray-900">{product.price}</span>
                    {product.priceUnit && (
                      <span className="text-lg text-gray-600">{product.priceUnit}</span>
                    )}
                    {product.originalPrice && (
                      <span className="text-xl text-gray-500 line-through ml-2">{product.originalPrice}</span>
                    )}
                  </div>

                  {/* Note */}
                  {product.note && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6">
                      <p className="text-sm text-blue-800 font-semibold">
                        <i className="ri-information-line mr-2"></i>
                        {product.note}
                      </p>
                    </div>
                  )}

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-3">What's Included:</h4>
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-green-500 text-lg"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <Button 
                    variant="primary"
                    size="lg"
                    className="w-full py-4 font-bold whitespace-nowrap"
                    onClick={() => handleCTA(product.link)}
                  >
                    {product.cta}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons & Custom Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Additional Services & Add-ons
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your security with specialized services tailored to your unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((addon, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{addon.name}</h3>
                <p className="text-gray-600 mb-4">{addon.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-red-600">{addon.pricing}</span>
                  <button 
                    onClick={() => navigate('/schedule-call')}
                    className="text-sm text-gray-700 hover:text-red-600 font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    Learn More
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-600 mb-6">
              Need a custom solution? We'll create a security package tailored to your specific requirements.
            </p>
            <Button 
              variant="outline" 
              size="lg" 
              className="px-12 py-4 font-bold whitespace-nowrap"
              onClick={() => navigate('/schedule-call')}
            >
              Schedule Custom Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Volume Discounts */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl shadow-2xl overflow-hidden text-white p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-team-line text-5xl"></i>
              </div>
              <h2 className="text-4xl font-bold mb-4">Enterprise & Volume Discounts</h2>
              <p className="text-xl text-white/90">
                Protecting a larger organization? We offer special pricing for teams of 20+ users.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">20-49 Users</div>
                <p className="text-white/80">10% discount</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">50-99 Users</div>
                <p className="text-white/80">15% discount</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">100+ Users</div>
                <p className="text-white/80">Custom pricing</p>
              </div>
            </div>

            <div className="text-center">
              <Button 
                variant="secondary" 
                size="lg" 
                className="px-12 py-4 font-bold whitespace-nowrap"
                onClick={() => navigate('/schedule-call')}
              >
                Request Enterprise Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Compare All Plans
            </h2>
            <p className="text-xl text-gray-600">
              See which features are included in each service
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white shadow-lg rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-4 font-bold text-gray-900">Feature</th>
                  <th className="text-center p-4 font-bold text-gray-900">Free Trial</th>
                  <th className="text-center p-4 font-bold text-gray-900 bg-red-50">Managed Services</th>
                  <th className="text-center p-4 font-bold text-gray-900">Assessment</th>
                  <th className="text-center p-4 font-bold text-gray-900">Training</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: '24/7 Security Monitoring', trial: false, managed: true, assessment: false, training: false },
                  { feature: 'Phishing Simulations', trial: true, managed: true, assessment: false, training: false },
                  { feature: 'Security Training', trial: false, managed: true, assessment: false, training: true },
                  { feature: 'Vulnerability Scanning', trial: false, managed: true, assessment: true, training: false },
                  { feature: 'Incident Response', trial: false, managed: true, assessment: false, training: false },
                  { feature: 'Compliance Documentation', trial: false, managed: true, assessment: true, training: true },
                  { feature: 'Risk Assessment Report', trial: false, managed: false, assessment: true, training: false },
                  { feature: 'Interactive Training Modules', trial: false, managed: true, assessment: false, training: true },
                  { feature: 'Dedicated Support', trial: false, managed: true, assessment: true, training: false }
                ].map((row, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="p-4 text-gray-700 font-semibold">{row.feature}</td>
                    <td className="text-center p-4">
                      {row.trial ? (
                        <i className="ri-check-line text-green-500 text-2xl"></i>
                      ) : (
                        <i className="ri-close-line text-gray-300 text-2xl"></i>
                      )}
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      {row.managed ? (
                        <i className="ri-check-line text-green-500 text-2xl"></i>
                      ) : (
                        <i className="ri-close-line text-gray-300 text-2xl"></i>
                      )}
                    </td>
                    <td className="text-center p-4">
                      {row.assessment ? (
                        <i className="ri-check-line text-green-500 text-2xl"></i>
                      ) : (
                        <i className="ri-close-line text-gray-300 text-2xl"></i>
                      )}
                    </td>
                    <td className="text-center p-4">
                      {row.training ? (
                        <i className="ri-check-line text-green-500 text-2xl"></i>
                      ) : (
                        <i className="ri-close-line text-gray-300 text-2xl"></i>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Pricing Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'Are there any setup fees or hidden costs?',
                answer: 'No. All our pricing is transparent with no setup fees, hidden costs, or surprise charges. What you see is what you pay.'
              },
              {
                question: 'Can I switch plans or cancel anytime?',
                answer: 'Yes. You can upgrade, downgrade, or cancel your subscription at any time. For monthly plans, there are no long-term contracts or cancellation fees.'
              },
              {
                question: 'Do you offer discounts for annual commitments?',
                answer: 'Yes. Our training services offer 50% savings with annual billing. Contact us for annual pricing on managed services and other products.'
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major credit cards, ACH transfers, and can invoice for annual contracts. Enterprise customers can arrange custom payment terms.'
              },
              {
                question: 'Is there a money-back guarantee?',
                answer: 'Yes. We offer a 30-day money-back guarantee on all subscription services. If you\'re not satisfied, we\'ll refund your payment, no questions asked.'
              },
              {
                question: 'Can I combine multiple services?',
                answer: 'Absolutely. Many customers combine our managed services with training or add a risk assessment. We can create custom bundles with special pricing for multiple services.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Protect Your Business?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start with a phishing simulation and free trial or speak with our security experts to find the perfect solution for your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              variant="secondary" 
              size="lg" 
              className="px-12 py-4 font-bold whitespace-nowrap text-lg"
              onClick={() => navigate('/purchase/phishing-simulation')}
            >
              Get Phishing Simulation
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="px-12 py-4 font-bold whitespace-nowrap text-lg border-white text-white hover:bg-white hover:text-red-600"
              onClick={() => navigate('/schedule-call')}
            >
              Talk to Sales
            </Button>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at (555) 123-4567 or email info@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;

